import React from "react";
import { ImageIcon, ExternalLink } from "lucide-react";
import { resolveMediaUrl } from "../../../../lib/utils";
import { formatPermitDateTime } from "./permitHelpers";

export const PermitChecklistPhotosList = ({
  images = [],
  title = "Uploaded Images",
  className = "",
}) => {
  if (!images || images.length === 0) return null;

  return (
    <div className={`mt-3 ${className}`}>
      <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-slate-600">
        <ImageIcon className="h-3.5 w-3.5 text-slate-500" />
        <span>{title} ({images.length})</span>
      </p>

      <div className="flex flex-wrap gap-3">
        {images.map((img) => {
          const url = resolveMediaUrl(img?.file_url || img?.file || "");
          if (!url) return null;

          const isMaker =
            (img.uploaded_by_role &&
              img.uploaded_by_role.toLowerCase().includes("maker")) ||
            (!img.workflow_log &&
              (!img.uploaded_by_role ||
                img.uploaded_by_role.toLowerCase() === "maker"));

          const roleLabel = img.uploaded_by_role || (isMaker ? "Maker" : "Checker");
          const uploaderName =
            img.uploaded_by_name ||
            (isMaker ? "Maker" : "Checker");

          const formattedDate = img.created_at
            ? formatPermitDateTime(img.created_at)
            : "";

          return (
            <div
              key={img.id || url}
              className="flex w-36 sm:w-40 flex-col rounded-lg border border-slate-200 bg-white p-2 shadow-xs transition hover:shadow-md"
            >
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block h-24 w-full overflow-hidden rounded-md bg-slate-100 border border-slate-100"
                title={`View full image uploaded by ${uploaderName}`}
              >
                <img
                  src={url}
                  alt={`Checklist photo by ${uploaderName}`}
                  className="h-full w-full object-cover transition duration-200 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-0 transition-opacity group-hover:opacity-100">
                  <span className="flex items-center gap-1 rounded bg-black/70 px-2 py-0.5 text-[10px] font-medium text-white shadow">
                    <ExternalLink className="h-2.5 w-2.5" />
                    View
                  </span>
                </div>
              </a>

              <div className="mt-2 flex flex-col gap-1">
                <div className="flex items-center justify-between gap-1">
                  <span
                    className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide border ${
                      isMaker
                        ? "border-amber-200 bg-amber-50 text-amber-700"
                        : "border-indigo-200 bg-indigo-50 text-indigo-700"
                    }`}
                  >
                    {roleLabel}
                  </span>
                </div>

                <div className="flex flex-col min-w-0">
                  <span
                    className="truncate text-xs font-semibold text-slate-800"
                    title={uploaderName}
                  >
                    {uploaderName}
                  </span>
                  {formattedDate && (
                    <span
                      className="mt-0.5 truncate text-[10px] text-slate-400"
                      title={formattedDate}
                    >
                      {formattedDate}
                    </span>
                  )}
                </div>

                {img.remarks && (
                  <p
                    className="mt-1 line-clamp-2 text-[10px] text-slate-500 italic bg-slate-50 p-1 rounded border border-slate-100"
                    title={img.remarks}
                  >
                    {img.remarks}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PermitChecklistPhotosList;
